import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './SafetyPacksPageV3.css'

import { DomainContext } from '../../domain-repository/DomainRepository'

import withLang from '../../withLang'
import PageHeader from '../../components/PageHeader'
import { FooterWithPrice } from '../../components/Footer'
import TrackingPage from '../../components/TrackingPage'
import { SimpleCheckbox } from '../../components/legal-information-section/LegalInformation'

import { savePackBasicAction } from '../../services/Metrics'
import { useSafetyPacksPageV3 } from './useSafetyPacksPageV3'

import { buildPackRows, depositHint, depositLabel, formatPackPrice, getPackConfig, packRowDescription } from '../../utils/packs-utils'

const TOTAL_STARS = 3

/**
 * Packs de seguranca novos no kiosk virtual.
 *
 * Equivalente mobile do InsurancePageV3 do kiosk fisico: serve as duas vertentes com o mesmo
 * componente e a mesma rota (4 cartoes no inclusive, 5 no exclusive, onde o Pack Basico e um cartao
 * em vez de uma pagina segregada). Coexiste com /safety-packs (legacy): a escolha e feita no
 * backend, em filterDisplayablePages, atraves de reservation.newSafetyPacksVariant.
 *
 * Ao contrario do kiosk fisico, os cartoes estao empilhados e fechados por omissao (acordeao): as 9
 * linhas so aparecem no cartao aberto e a descricao de cada linha abre inline.
 */
function SafetyPacksPageV3({ history }) {
  const { t } = useTranslation()

  const {
    flowNextRoute,
    flowPreviousRoute,
    reservation,
    reservationExtras,
    getExcessForExtras,
    insuranceSelectedPack,
    rentalAgreementQuoteValue,
    saveSessionExtras,
    setPackBasicDecided
  } = useContext(DomainContext)

  const {
    packs,
    reservationPack,
    packsWithoutReservation,
    selectedPack,
    onSelectClick,
    isSelected,
    isExclusiveVariant,
    packBasicInReservation
  } = useSafetyPacksPageV3(onNext, {
    reservationPackName: t('insuranceV3NoPackName'),
    basicPackName: t('PEName')
  })

  // Um cartao aberto de cada vez, como no design; nenhum aberto a entrada.
  const [openPack, setOpenPack] = useState(null)
  // Descricao de uma linha (ou da franquia/caucao) aberta de cada vez, dentro do cartao.
  const [openDetail, setOpenDetail] = useState(null)

  // Escolher a opcao base numa reserva exclusive e recusar o Pack Basico: mantem-se o reconhecimento
  // que a /exclusive-reservation exigia, agora no proprio ecra de packs.
  const [acknowledgeReject, setAcknowledgeReject] = useState(false)

  const isBaseSelected = Boolean(reservationPack) && isSelected(reservationPack)

  // Com o PE ja na reserva o cartao base E o Pack Basico: nao ha nada para recusar, nem
  // reconhecimento a pedir.
  const rejectedPackBasic = isExclusiveVariant && isBaseSelected && !packBasicInReservation
  const needsAcknowledge = rejectedPackBasic

  const currentExcess = useMemo(() => getExcessForExtras(reservationExtras), [reservationExtras]) // eslint-disable-line react-hooks/exhaustive-deps

  const orderedPacks = useMemo(() => {
    return reservationPack ? [reservationPack, ...packsWithoutReservation] : packsWithoutReservation
  }, [reservationPack, packsWithoutReservation])

  function onNext(forceNext) {
    const mappedExtras = packsWithoutReservation.map(pack => ({
      extraId: pack.extraId,
      price: pack.totalPrice,
      acquired: isSelected(pack),
      removedInReview: false,
      pageId: PageId
    }))

    saveSessionExtras(mappedExtras)

    // A aceitacao/recusa do Pack Basico passou a ser decidida aqui, ja que
    // /exclusive-reservation deixou de aparecer neste flow. So conta quando o cliente chegou mesmo a
    // ver os cartoes: o hook tambem chama onNext(true) para saltar a pagina, e nesse caso nao houve
    // escolha nenhuma para registar.
    if (isExclusiveVariant && packs.length > 0) {
      // A partir daqui a ausencia do PE na selecao passa a poder ser lida como recusa: e o que faz os
      // extras bloqueados (extrasBlocked) desaparecerem no resto do flow. Enquanto o cliente nao
      // chegou a ver os cartoes nao houve recusa nenhuma, logo nao ha nada a bloquear.
      setPackBasicDecided(true)
      savePackBasicAction(!rejectedPackBasic)
    }

    if (!forceNext && insuranceSelectedPack === null) return null
    return history.location.state && history.location.state.continueUrl
      ? history.push(history.location.state.continueUrl)
      : history.push(flowNextRoute())
  }

  function toggleDetail(id) {
    setOpenDetail(current => (current === id ? null : id))
  }

  function togglePack(pack) {
    setOpenPack(current => (current === pack.extraId ? null : pack.extraId))
    setOpenDetail(null)
  }

  return packs.length > 0 ? (
    <div style={{ '--vh': '1vh' }}>
      <PageHeader
        title={t('safety')}
        active={2}
        progress={'50%'}
        handleLogoClick={() => history.push('/start')}
      />
      <main className='page-layout'>
        <div className="container grow">
          <div className="main table-content" style={{ height: '100%' }}>
            <div className='container-small mx-auto w-100 safety-packs-v3'>
              <h1 className="text-center mt-4 safety-packs-v3__title">{t('insuranceV3Title')}</h1>
              <div className='safety-packs-v3__list'>
                {orderedPacks.map(pack => (
                  <PackCardV3
                    key={pack.extraId}
                    pack={pack}
                    t={t}
                    currentExcess={currentExcess}
                    isExclusiveVariant={isExclusiveVariant}
                    fullVehicleExcess={reservation && reservation.excessNoPriceReduction}
                    isOpen={openPack === pack.extraId}
                    onToggle={() => togglePack(pack)}
                    isSelected={isSelected(pack)}
                    onSelect={() => onSelectClick(isSelected(pack) ? reservationPack : pack)}
                    openDetail={openDetail}
                    onToggleDetail={toggleDetail}
                  />
                ))}
              </div>
              {needsAcknowledge ? (
                <div className="safety-packs-v3__acknowledge">
                  <SimpleCheckbox
                    value={acknowledgeReject}
                    onChange={setAcknowledgeReject}
                    label={t('rejectPackBasicAcknowledge')}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <FooterWithPrice
          totalPrice={rentalAgreementQuoteValue}
          totalPriceLabel={t('total')}
          handleNextClick={() => onNext(true)}
          nextDisabled={!(selectedPack !== null && (!needsAcknowledge || acknowledgeReject))}
          nextHighlighted={selectedPack !== null}
          handlePrevClick={() => history.location.state && history.location.state.backUrl
            ? history.replace(history.location.state.backUrl)
            : flowPreviousRoute(history)}
        />
      </main>
    </div>
  ) : null
}

export const PageId = 2013
export default withLang(TrackingPage(SafetyPacksPageV3, PageId))

function PackCardV3({
  pack,
  t,
  currentExcess,
  isExclusiveVariant,
  fullVehicleExcess,
  isOpen,
  onToggle,
  isSelected,
  onSelect,
  openDetail,
  onToggleDetail
}) {
  const packConfig = getPackConfig(pack.extraId)
  const isBase = Boolean(pack.isReservationPack)

  // GKIOSK-1889 - o cartao base pode ser o "Sem Pack" ou, quando o PE ja vem na reserva, o proprio
  // Pack Basico. Tudo o que e especifico do "Sem Pack" (nome invertido, franquia da viatura toda,
  // vermelho) segue este flag e nao o isBase; o resto (preco escondido, botao "Incluido") vale para
  // os dois, porque em ambos os casos o cartao representa o que a reserva ja traz.
  const isNoPackCard = isBase && !pack.isBasicPack

  const rows = useMemo(() => buildPackRows(pack, t), [pack, t])

  const packName = isNoPackCard ? pack.name : t(`${pack.extraId}Name`, pack.name)

  // No design o cartao base le-se "Sem" + "Pack": a ordem das duas linhas e a inversa dos restantes
  // cartoes ("Pack" + nome do pack), pelo que a linha fina tambem tem de ser traduzida.
  const nameLabel = isNoPackCard ? t('insuranceV3NoPackLabel') : 'Pack'

  const excessValue = pack.excess !== null && pack.excess !== undefined ? pack.excess : currentExcess

  // Numa reserva exclusive sem pack nenhum o cliente responde pela viatura toda: o design escreve-o
  // por extenso em vez de mostrar um numero, e o valor concreto fica na descricao do (i). Nao se
  // aplica quando o cartao base e o Pack Basico, que ja traz a franquia do CDW.
  const showsFullVehicleExcess = isExclusiveVariant && isNoPackCard

  const showNoDiscount =
    pack.showNoDiscountValue && Number(pack.noDiscountValue) > Number(pack.price) && Number(pack.price) > 0

  // O fundo do cartao nao depende da selecao (quem sinaliza o pack escolhido e o botao "Adicionado"):
  // no design os fundos sao fixos por pack - cinza no Protecao (emphasized) e creme + borda laranja
  // no Super Protecao (recommended).
  const cardClasses = [
    'safety-packs-v3__card',
    packConfig && packConfig.emphasized ? 'safety-packs-v3__card--emphasized' : '',
    packConfig && packConfig.recommended ? 'safety-packs-v3__card--recommended' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cardClasses}>
      <div className="safety-packs-v3__chips">
        {packConfig && packConfig.recommended ? (
          <div className="safety-packs-v3__chip safety-packs-v3__chip--recommended">{t('recomended')}</div>
        ) : null}
        {packConfig && packConfig.discountBadge ? (
          <div className="safety-packs-v3__chip safety-packs-v3__chip--discount">
            {packConfig.discountBadge}% {t('insuranceV3Discount')}
          </div>
        ) : null}
      </div>

      <div className="safety-packs-v3__head" onClick={onToggle}>
        {/* O cartao "Sem Pack" do exclusive nao tem estrelas nenhumas (nem vazias) no design; a linha
            fica na mesma no DOM para os titulos dos cartoes alinharem. */}
        <PackStarsV3 filled={(packConfig && packConfig.stars) || 0} total={TOTAL_STARS} hidden={showsFullVehicleExcess} />
        <span className={`safety-packs-v3__chevron ${isOpen ? 'safety-packs-v3__chevron--open' : ''}`} />
      </div>

      <div className="safety-packs-v3__name" onClick={onToggle}>
        <div className="safety-packs-v3__name-label">{nameLabel}</div>
        <div className="safety-packs-v3__name-value">{packName}</div>
      </div>

      <PackValueRowV3
        id={`${pack.extraId}-excess`}
        label={showsFullVehicleExcess ? t('packExcessFullVehicleValue') : `${t('excess')} ${formatPackPrice(excessValue)}€`}
        hint={
          showsFullVehicleExcess
            ? t('packExcessFullVehicleValueExplanation', { value: formatPackPrice(fullVehicleExcess) })
            : t('excessExplanation')
        }
        theme={isNoPackCard ? 'negative' : 'positive'}
        openDetail={openDetail}
        onToggleDetail={onToggleDetail}
      />

      {/* A cor segue o desconto e nao o cartao: o Pack Basico do exclusive nao reduz a caucao (PE nao
          e um pack), pelo que mostra "Caucao total" a vermelho apesar de ter franquia a verde. */}
      <PackValueRowV3
        id={`${pack.extraId}-deposit`}
        label={depositLabel(pack, t)}
        hint={depositHint(pack, t)}
        theme={pack.auth1 && pack.auth1.withoutDiscountValue ? 'positive' : 'negative'}
        openDetail={openDetail}
        onToggleDetail={onToggleDetail}
      />

      {isOpen ? (
        <div className="safety-packs-v3__rows">
          {rows.map(row => (
            <PackProductRowV3
              key={row.id}
              id={`${pack.extraId}-${row.id}`}
              row={row}
              t={t}
              openDetail={openDetail}
              onToggleDetail={onToggleDetail}
            />
          ))}
        </div>
      ) : null}

      <div className="safety-packs-v3__footer">
        <div className="safety-packs-v3__price">
          {showNoDiscount ? (
            <div className="safety-packs-v3__price-no-discount" title={t('insuranceV3NoDiscountLabel')}>
              € {formatPackPrice(pack.noDiscountValue)}/{t('day')}
            </div>
          ) : null}
          {isBase ? null : (
            <div className="safety-packs-v3__price-day">€ {formatPackPrice(pack.price)}/ {t('day')}</div>
          )}
        </div>
        {isBase ? (
          <div className="safety-packs-v3__button safety-packs-v3__button--included">{t('insuranceV3Included')}</div>
        ) : (
          <div
            id={`insurance-pack-${pack.extraId}`}
            onClick={onSelect}
            className={`safety-packs-v3__button ${
              isSelected ? 'safety-packs-v3__button--added' : 'safety-packs-v3__button--add'
            }`}
          >
            {isSelected ? (
              <>
                {t('insuranceV3Added')}
                <AddedCheckIcon />
              </>
            ) : (
              t('add')
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function PackStarsV3({ filled, total, hidden }) {
  const slots = hidden ? [] : Array.from({ length: total }, (_, i) => i + 1)

  return (
    <div className="safety-packs-v3__stars">
      {slots.map(slot => (
        <StarIcon key={slot} filled={slot <= filled} />
      ))}
    </div>
  )
}

function StarIcon({ filled }) {
  return (
    <svg className="safety-packs-v3__star" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2.5l2.95 5.98 6.6.96-4.78 4.66 1.13 6.57L12 17.57l-5.9 3.1 1.13-6.57L2.45 9.44l6.6-.96L12 2.5z"
        fill={filled ? '#303030' : 'none'}
        stroke="#303030"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function AddedCheckIcon() {
  return (
    <svg className="safety-packs-v3__button-check" viewBox="0 0 24 19" aria-hidden="true">
      <path d="M-4.25 3 L9.75 15.5 L25.85 -3" fill="none" stroke="currentColor" strokeWidth="6.5" />
    </svg>
  )
}

// Desenhado inline (em vez de ico-info.svg, que tem a cor fixa) para herdar a cor da linha.
function InfoIcon() {
  return (
    <svg className="safety-packs-v3__info-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="7" r="1.4" fill="currentColor" />
      <path d="M12 10.6v7.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

// Franquia / caucao: o (i) abre a explicacao inline, por baixo da linha.
function PackValueRowV3({ id, label, hint, theme, openDetail, onToggleDetail }) {
  return (
    <>
      <div
        className={`safety-packs-v3__value-row safety-packs-v3__value-row--${theme}`}
        onClick={() => onToggleDetail(id)}
      >
        <div>{label}</div>
        <InfoIcon />
      </div>
      {openDetail === id ? <div className="safety-packs-v3__detail">{hint}</div> : null}
    </>
  )
}

// O "+" de qualquer linha abre a descricao dessa linha - inclusive nas inativas, onde serve para
// explicar o que o cliente ficaria a ganhar. A copy e a mesma do kiosk fisico (packRowDescription).
function PackProductRowV3({ id, row, t, openDetail, onToggleDetail }) {
  return (
    <>
      <div
        className={`safety-packs-v3__row ${row.active ? '' : 'safety-packs-v3__row--inactive'}`}
        onClick={() => onToggleDetail(id)}
      >
        <span className="safety-packs-v3__row-check" />
        <div className="safety-packs-v3__row-label">{row.label}</div>
        <div className="safety-packs-v3__row-plus">{openDetail === id ? '−' : '+'}</div>
      </div>
      {openDetail === id ? <div className="safety-packs-v3__detail">{packRowDescription(row, t)}</div> : null}
    </>
  )
}
